import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { User, Trash2 } from 'lucide-react'
import { api } from '@/services/api'
import { DeleteModal } from '@/components/ui/delete-modal'

export interface EditProjectManagerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  projectManager: { id: string; name: string; email: string } | null
}

export function EditProjectManagerModal({
  open,
  onOpenChange,
  onSuccess,
  projectManager,
}: EditProjectManagerModalProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Update form when project manager changes
  useEffect(() => {
    if (projectManager) {
      setName(projectManager.name || '')
      setEmail(projectManager.email || '')
    }
  }, [projectManager])

  const handleSubmit = async () => {
    if (!projectManager?.id) return
    
    if (!name.trim()) {
      alert('Veuillez entrer un nom')
      return
    }
    
    if (!email.trim() || !isValidEmail(email.trim())) {
      alert('Veuillez entrer un email valide')
      return
    }

    setSaving(true)
    try {
      // Update project manager details (name and email)
      await api.put(`/users/${projectManager.id}`, {
        nom: name.trim(),
        email: email.trim()
      })
      
      onSuccess()
      onOpenChange(false)
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du chef de projet:', error)
      const errorMessage = error?.response?.data?.detail || 'Erreur lors de la mise à jour du chef de projet'
      alert(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!projectManager?.id) return
    
    setIsDeleting(true)
    try {
      // Delete the user (project manager)
      await api.delete(`/users/${projectManager.id}`)
      
      // The project will need a new project manager, but we'll let the user handle that
      // by navigating back or showing an error
      onSuccess()
      onOpenChange(false)
      setShowDeleteModal(false)
    } catch (error: any) {
      console.error('Erreur lors de la suppression du chef de projet:', error)
      const errorMessage = error?.response?.data?.detail || 'Erreur lors de la suppression du chef de projet'
      alert(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        size="md"
        hideHeader={true}
        contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
        footer={
          <div className="flex gap-3 pt-4 justify-between">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(true)}
              disabled={saving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={saving}
                disabled={!name.trim() || !email.trim() || !isValidEmail(email.trim())}
                className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Modifier le chef de projet</h2>
              <p className="text-sm text-gray-500 mt-1">
                Mettez à jour les informations du chef de projet ou changez-le
              </p>
            </div>
          </div>

          <FormField label="Nom" required>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nom du chef de projet"
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
              />
            </div>
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemple.com"
              className={`bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent ${
                email && !isValidEmail(email) ? 'border-red-300' : ''
              }`}
            />
          </FormField>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 Note :</strong> Pour changer le chef de projet (assigner un autre chef de projet au projet), utilisez le bouton "Modifier" du projet et sélectionnez un autre chef de projet dans la liste.
            </p>
          </div>
        </div>
      </Modal>

      <DeleteModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleDelete}
        title="Supprimer le chef de projet"
        itemName={projectManager?.name}
        isLoading={isDeleting}
        description="Cette action supprimera définitivement le chef de projet. Assurez-vous d'avoir assigné un nouveau chef de projet au projet avant de supprimer celui-ci."
      />
    </>
  )
}
